// AGC: Quote (Cotação) Service — client-side wrapper around /api/v1/quotes.
// AGC: Quotes are generic supplier requests saved to Firestore, convertible to pedidos.

import { api } from './api'
import type {
  Quote,
  QuoteStatus,
  CreateQuoteData,
  ConvertQuoteData,
  PurchaseOrder,
} from '../types/inventory'

const BASE = '/api/v1/quotes'

interface QuoteResponse { success: true; data: Quote }
interface QuoteListResponse { success: true; data: Quote[] }
interface ConvertResponse { success: true; data: { quote: Quote; order: PurchaseOrder } }

export async function createQuoteRequest(
  _userId: string,
  _farmId: string,
  data: CreateQuoteData,
): Promise<Quote> {
  const res = await api.post<QuoteResponse>(BASE, data)
  return res.data
}

export async function getQuotes(
  _userId: string,
  _farmId: string,
  status?: QuoteStatus,
): Promise<Quote[]> {
  const path = status ? `${BASE}?status=${encodeURIComponent(status)}` : BASE
  const res = await api.get<QuoteListResponse>(path)
  return res.data
}

export async function getQuoteById(
  _userId: string,
  _farmId: string,
  quoteId: string,
): Promise<Quote | null> {
  try {
    const res = await api.get<QuoteResponse>(`${BASE}/${quoteId}`)
    return res.data
  } catch (error) {
    if (error instanceof Error && 'status' in error && (error as { status: number }).status === 404) {
      return null
    }
    throw error
  }
}

export async function updateQuoteRequest(
  _userId: string,
  _farmId: string,
  quoteId: string,
  data: Partial<Omit<Quote, 'id' | 'createdAt' | 'createdBy'>>,
): Promise<void> {
  await api.patch<QuoteResponse>(`${BASE}/${quoteId}`, data)
}

export async function sendQuoteRequest(
  _userId: string,
  _farmId: string,
  quoteId: string,
  sharedVia: 'whatsapp' | 'email' | 'other',
): Promise<void> {
  await api.post<QuoteResponse>(`${BASE}/${quoteId}/send`, { sharedVia })
}

export async function cancelQuoteRequest(
  _userId: string,
  _farmId: string,
  quoteId: string,
  reason?: string,
): Promise<void> {
  await api.post<QuoteResponse>(`${BASE}/${quoteId}/cancel`, { reason })
}

export async function deleteQuoteRequest(
  _userId: string,
  _farmId: string,
  quoteId: string,
): Promise<void> {
  await api.delete<{ success: boolean }>(`${BASE}/${quoteId}`)
}

export async function convertQuoteToOrder(
  _userId: string,
  _farmId: string,
  quoteId: string,
  data: ConvertQuoteData,
): Promise<{ quote: Quote; order: PurchaseOrder }> {
  const res = await api.post<ConvertResponse>(`${BASE}/${quoteId}/convert`, data)
  return res.data
}
